import { useState, useEffect } from 'react';
import { CartItem } from '@app/components/ShoppingCart';
import { Footer } from '@app/components/shared/Footer';
import { AddressForm } from '@app/components/shared/AddressForm';
import { birdieHeaderLogo, birdie, trashBinIcon } from '@/assets';
import { 
  GiftHandlingOrderData, 
  GiftHandlingAddress, 
  GiftHandlingContactInfo,
  CreateGiftTaskRequest,
  CreateGiftTaskResponse 
} from '@shared/types/giftHandlingTypes';

// Use shared types
type Address = GiftHandlingAddress;
type ContactInfo = GiftHandlingContactInfo;

interface ShoppingCartPageProps {
  cartItems: CartItem[];
  contactInfo: ContactInfo;
  token: string;
  onBack: () => void;
  onSubmit: (data: GiftHandlingOrderData) => void;
  onUpdateQuantity: (giftId: number, quantity: number) => void;
  onRemoveItem: (giftId: number) => void;
}

export default function ShoppingCartPage({ 
  cartItems, 
  contactInfo, 
  token,
  onBack, 
  onSubmit,
  onUpdateQuantity,
  onRemoveItem
}: ShoppingCartPageProps) {
  const [selectedAddresses, setSelectedAddresses] = useState<{ [key: number]: string }>({});
  const [showAddressForm, setShowAddressForm] = useState<{ [key: number]: boolean }>({});
  const [newAddresses, setNewAddresses] = useState<{ [key: number]: Address }>({});
  const [comments, setComments] = useState<{ [key: number]: string }>({});
  const [wrappingPrefs, setWrappingPrefs] = useState<{ [key: number]: boolean }>({});
  const [accountInfo, setAccountInfo] = useState<ContactInfo>(contactInfo);
  const [sameAddress, setSameAddress] = useState(false);
  const [predefinedAddresses, setPredefinedAddresses] = useState<Address[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [addressesError, setAddressesError] = useState<string | null>(null);
  const [savingAddressIndex, setSavingAddressIndex] = useState<number | null>(null);

  // Fetch addresses on component mount
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setAddressesLoading(true);
        const response = await fetch(`/api/public/contact-addresses/${token}`);
        const result = await response.json();
        
        if (result.success) {
          setPredefinedAddresses(result.data.addresses.map((addr: any) => ({
            id: addr.id.toString(),
            label: addr.name,
            street: addr.street,
            city: addr.city,
            state: addr.state,
            zipCode: addr.zipCode
          })));
        } else {
          setAddressesError(result.error || 'Failed to fetch addresses');
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setAddressesError('Network error. Please try again.');
      } finally {
        setAddressesLoading(false);
      }
    };
    
    fetchAddresses();
  }, [token]);

  // Auto-exit when cart becomes empty
  useEffect(() => {
    if (cartItems.length === 0) {
      onBack();
    }
  }, [cartItems.length, onBack]);

  // When "Send all gifts to the same address" is checked and there's already a selected shared address,
  // apply it to all individual gift addresses
  useEffect(() => {
    if (sameAddress && selectedAddresses[-1] && selectedAddresses[-1] !== 'new' && selectedAddresses[-1] !== '') {
      const updates: { [key: number]: string } = {};
      const formUpdates: { [key: number]: boolean } = {};
      cartItems.forEach((_, index) => {
        updates[index] = selectedAddresses[-1];
        formUpdates[index] = false;
      });
      setSelectedAddresses(prev => ({ ...prev, ...updates }));
      setShowAddressForm(prev => ({ ...prev, ...formUpdates }));
    }
  }, [sameAddress, selectedAddresses[-1], cartItems]);

  const handleAddressSelect = (itemIndex: number, addressId: string) => {
    if (addressId === 'new') {
      setShowAddressForm(prev => ({ ...prev, [itemIndex]: true }));
      setSelectedAddresses(prev => ({ ...prev, [itemIndex]: addressId }));
    } else {
      setShowAddressForm(prev => ({ ...prev, [itemIndex]: false }));
      setSelectedAddresses(prev => ({ ...prev, [itemIndex]: addressId }));
    }

    // If this is the shared address (index -1) and "Send all gifts to the same address" is checked,
    // update all individual gift addresses to match the selected shared address
    if (itemIndex === -1 && sameAddress && addressId !== 'new' && addressId !== '') {
      // Set all individual gift addresses to the same address
      const updates: { [key: number]: string } = {};
      cartItems.forEach((_, index) => {
        updates[index] = addressId;
      });
      setSelectedAddresses(prev => ({ ...prev, ...updates }));
      
      // Clear any "new address" forms for individual items since they now use predefined addresses
      const formUpdates: { [key: number]: boolean } = {};
      cartItems.forEach((_, index) => {
        formUpdates[index] = false;
      });
      setShowAddressForm(prev => ({ ...prev, ...formUpdates }));
    }
  };

  const handleSaveNewAddress = async (itemIndex: number, addressData?: Address) => {
    const newAddress = addressData || newAddresses[itemIndex];
    
    // Debug logging
    console.log('Saving address:', newAddress);
    console.log('Address validation:', {
      hasLabel: !!newAddress?.label,
      hasStreet: !!newAddress?.street,
      hasCity: !!newAddress?.city,
      hasState: !!newAddress?.state,
      hasZip: !!newAddress?.zipCode
    });
    
    // Validate required fields with specific error messages
    const missingFields = [];
    if (!newAddress) {
      alert('Address data is missing. Please try again.');
      return;
    }
    if (!newAddress.label?.trim()) missingFields.push('Address Name');
    if (!newAddress.street?.trim()) missingFields.push('Street Address');
    if (!newAddress.city?.trim()) missingFields.push('City');
    if (!newAddress.state?.trim()) missingFields.push('State');
    if (!newAddress.zipCode?.trim()) missingFields.push('Zip Code');
    
    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }

    setSavingAddressIndex(itemIndex);

    try {
      const response = await fetch(`/api/public/contact-addresses/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newAddress.label,
          street: newAddress.street,
          city: newAddress.city,
          state: newAddress.state,
          zipCode: newAddress.zipCode
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Add the new address to predefined addresses using data returned from server
        const savedAddress = {
          id: result.data.id.toString(),
          label: result.data.name,
          street: result.data.street,
          city: result.data.city,
          state: result.data.state,
          zipCode: result.data.zipCode
        };
        
        setPredefinedAddresses(prev => [...prev, savedAddress]);
        
        // Select the new address and hide the form
        console.log(`Auto-selecting saved address ${savedAddress.id} for item ${itemIndex}`);
        setSelectedAddresses(prev => ({ ...prev, [itemIndex]: savedAddress.id }));
        setShowAddressForm(prev => ({ ...prev, [itemIndex]: false }));
        
        // If this is the shared address (index -1) and "Send all gifts to the same address" is checked,
        // update all individual gift addresses to match the newly saved shared address
        if (itemIndex === -1 && sameAddress) {
          const updates: { [key: number]: string } = {};
          const formUpdates: { [key: number]: boolean } = {};
          cartItems.forEach((_, index) => {
            updates[index] = savedAddress.id;
            formUpdates[index] = false;
          });
          setSelectedAddresses(prev => ({ ...prev, ...updates }));
          setShowAddressForm(prev => ({ ...prev, ...formUpdates }));
        }
        
        // Clear the new address form data
        setNewAddresses(prev => {
          const newState = { ...prev };
          delete newState[itemIndex];
          return newState;
        });
      } else {
        alert(result.error || 'Failed to save address');
      }
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Network error. Please try again.');
    } finally {
      setSavingAddressIndex(null);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Validate required fields
    const missingAddresses = cartItems.some((_, index) => {
      if (sameAddress) {
        return !selectedAddresses[-1] || selectedAddresses[-1] === '';
      } else {
        return !selectedAddresses[index] || selectedAddresses[index] === '';
      }
    });

    if (missingAddresses) {
      alert('Please select shipping addresses for all items.');
      return;
    }

    if (!accountInfo.email || !accountInfo.phone) {
      alert('Please provide your email and phone number.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Process and submit the order
      const orderData: GiftHandlingOrderData = {
        items: cartItems.map((item, index) => {
          // If using same address for all items, use the shared address (index -1)
          let shippingAddress;
          if (sameAddress) {
            shippingAddress = selectedAddresses[-1] === 'new' 
              ? newAddresses[-1] 
              : predefinedAddresses.find(addr => addr.id === selectedAddresses[-1]);
          } else {
            shippingAddress = selectedAddresses[index] === 'new' 
              ? newAddresses[index] 
              : predefinedAddresses.find(addr => addr.id === selectedAddresses[index]);
          }

          return {
            ...item,
            comments: comments[index] || '',
            shippingAddress,
            wrapped: wrappingPrefs[index] || false
          };
        }),
        contact: accountInfo,
        sameAddress
      };

      // Create Salesforce task
      const requestBody: CreateGiftTaskRequest = { orderData };
      
      const response = await fetch(`/api/public/create-gift-task/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result: CreateGiftTaskResponse = await response.json();

      if (result.success) {
        // Call the original onSubmit callback if provided
        onSubmit(orderData);
        alert('Your gift handling request has been submitted successfully! We\'ll contact you within 24 hours.');
        // Refresh the page to show updated gift status (grayed out, processed)
        window.location.reload();
      } else {
        alert(result.error || 'Failed to submit gift handling request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting gift handling request:', error);
      alert('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <img src={birdieHeaderLogo} alt="Birdie" className="h-8" />
        </div>
      </div>



      {/* Full Width Info Section */}
      <div className="w-full bg-gray-100 py-6 mb-6">
        {/* Back Button */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button 
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors text-sm cursor-pointer"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            BACK TO "GIFT RECOMMENDATION LIST"
          </button>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 flex items-center">
          {/* Bird SVG positioned absolutely */}
          <div className="flex-shrink-0 mr-6">
            <img src={birdie} alt="Birdie" className="w-24 h-24" />
          </div>
          
          {/* Text card */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl px-6 py-4 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Here's what's next!
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                 This is a request to have the Birdies order your gifts. We will assess timing and availability and be in touch within 24 hours with pricing and an online invoice.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Items */}
          <div className="lg:col-span-2 space-y-4">

            {/* Order Items Grouped by Recipient */}
            <div className="space-y-6">
              {Object.entries(cartItems.reduce(
                (groups, item) => {
                  const recipientName = item.recipient_name;
                  if (!groups[recipientName]) {
                    groups[recipientName] = [];
                  }
                  groups[recipientName].push(item);
                  return groups;
                },
                {} as Record<string, typeof cartItems>
              )).map(([recipientName, items]) => (
                <div key={recipientName} className="space-y-4">
                  {/* Recipient Header with Green Button */}
                  <div className="flex justify-end">
                    <button className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                      {recipientName}'s Gift
                    </button>
                  </div>

                  {/* Items for this recipient */}
                  {items.map((item) => {
                    const globalIndex = cartItems.findIndex(cartItem => cartItem.id === item.id);
                    return (
                      <div key={item.id} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start gap-4 mb-4">
                          <img 
                            src={item.image_data || item.image_url} 
                            alt={item.title}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-semibold text-gray-900 text-base">{item.title}</h3>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-lg font-bold text-gray-900">{item.price}</span>
                              <div className="flex items-center border rounded-lg overflow-hidden">
                                {item.quantity === 1 ? (
                                  <button 
                                    onClick={() => onRemoveItem(item.id)}
                                    className="p-1 hover:bg-gray-100 flex items-center justify-center" 
                                    title="Remove item"
                                  >
                                    <img src={trashBinIcon} alt="trash bin icon" className="w-3 h-3" />
                                  </button>
                                ) : (
                                  <button 
                                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                                    className="p-1 hover:bg-gray-100 flex items-center justify-center text-lg"
                                  >
                                    -
                                  </button>
                                )}
                                <span className="px-3 py-1 font-medium border-l border-r text-sm">{item.quantity}</span>
                                <button 
                                  onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                                  className="p-1 hover:bg-gray-100 flex items-center justify-center text-lg"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Comments */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Comments
                          </label>
                          <textarea
                            value={comments[globalIndex] || ''}
                            onChange={(e) => setComments(prev => ({ ...prev, [globalIndex]: e.target.value }))}
                            placeholder="Enter any product details such as size, color, etc, or shipping instructions"
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm"
                            rows={2}
                          />
                        </div>

                        {/* Shipping Address */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Shipping Address <span className="text-red-500">*Required</span>
                          </label>
                          {addressesError && (
                            <div className="mb-2 text-sm text-red-600 bg-red-50 p-2 rounded-md">
                              {addressesError}
                            </div>
                          )}
                          <div className="relative">
                            <select
                              value={selectedAddresses[globalIndex] || ''}
                              onChange={(e) => handleAddressSelect(globalIndex, e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white text-sm"
                              disabled={addressesLoading}
                            >
                              <option value="">
                                {addressesLoading ? 'Loading addresses...' : 'Select a shipping address'}
                              </option>
                              {!addressesLoading && predefinedAddresses.map(addr => (
                                <option key={addr.id} value={addr.id}>{addr.label}</option>
                              ))}
                              {!addressesLoading && (
                                <option value="new">Send to a new address</option>
                              )}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>

                          {/* Selected Address Display */}
                          {selectedAddresses[globalIndex] && selectedAddresses[globalIndex] !== 'new' && !showAddressForm[globalIndex] && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="text-sm text-gray-700">
                                {(() => {
                                  const selectedAddr = predefinedAddresses.find(addr => addr.id === selectedAddresses[globalIndex]);
                                  if (selectedAddr) {
                                    return `${selectedAddr.street}, ${selectedAddr.city}, ${selectedAddr.state} ${selectedAddr.zipCode}`;
                                  }
                                  return '';
                                })()}
                              </div>
                            </div>
                          )}

                          {/* New Address Form */}
                          {showAddressForm[globalIndex] && (
                            <div className="mt-4">
                              <AddressForm
                                address={{
                                  name: newAddresses[globalIndex]?.label || '',
                                  street: newAddresses[globalIndex]?.street || '',
                                  city: newAddresses[globalIndex]?.city || '',
                                  state: newAddresses[globalIndex]?.state || '',
                                  zipCode: newAddresses[globalIndex]?.zipCode || '',
                                }}
                                onSave={(addressData) => {
                                  // Create address object for validation and saving
                                  const addressToSave = {
                                    id: 'new',
                                    label: addressData.name || '',
                                    street: addressData.street || '',
                                    city: addressData.city || '',
                                    state: addressData.state || '',
                                    zipCode: addressData.zipCode || ''
                                  };
                                  
                                  // Update the newAddresses state with form data
                                  setNewAddresses(prev => ({
                                    ...prev,
                                    [globalIndex]: addressToSave
                                  }));
                                  
                                  // Pass the address data directly to avoid race condition
                                  handleSaveNewAddress(globalIndex, addressToSave);
                                }}
                                onCancel={() => {
                                  setShowAddressForm(prev => ({ ...prev, [globalIndex]: false }));
                                  setSelectedAddresses(prev => ({ ...prev, [globalIndex]: '' }));
                                }}
                                showDefaultOption={false}
                                submitButtonText="Save Address"
                                cancelButtonText="Cancel"
                                isLoading={savingAddressIndex === globalIndex}
                              />
                            </div>
                          )}
                        </div>

                        {/* Gift Wrapping */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`wrap-${globalIndex}`}
                            checked={wrappingPrefs[globalIndex] || false}
                            onChange={(e) => setWrappingPrefs(prev => ({ ...prev, [globalIndex]: e.target.checked }))}
                            className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                          />
                          <label htmlFor={`wrap-${globalIndex}`} className="ml-2 text-sm font-medium text-gray-700">
                            I want this gift wrapped
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-8">
              <h3 className="text-xl font-bold mb-6" style={{ color: '#8F6E98' }}>{accountInfo.name}</h3>
              
              <p className="text-gray-600 mb-6">
                Please review your contact information. We will communicate via email or phone as soon as your order is processed.
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Phone Number
                  </label>
                  <input
                    type="tel"
                    value={accountInfo.phone}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123 - 4567"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={accountInfo.email}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="jamesaddams@emailcompany.com"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sameAddress}
                    onChange={(e) => setSameAddress(e.target.checked)}
                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Send all gifts to the same address
                  </span>
                </label>

                {/* Address Selector - shown when "Send all gifts to the same address" is checked */}
                {sameAddress && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shipping Address <span className="text-red-500">*Required</span>
                    </label>
                    {addressesError && (
                      <div className="mb-2 text-sm text-red-600 bg-red-50 p-2 rounded-md">
                        {addressesError}
                      </div>
                    )}
                    <div className="relative">
                      <select
                        value={selectedAddresses[-1] || ''}
                        onChange={(e) => handleAddressSelect(-1, e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white text-sm"
                        disabled={addressesLoading}
                      >
                        <option value="">
                          {addressesLoading ? 'Loading addresses...' : 'Select a shipping address'}
                        </option>
                        {!addressesLoading && predefinedAddresses.map(addr => (
                          <option key={addr.id} value={addr.id}>{addr.label}</option>
                        ))}
                        {!addressesLoading && (
                          <option value="new">Send to a new address</option>
                        )}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Selected Address Display */}
                    {selectedAddresses[-1] && selectedAddresses[-1] !== 'new' && !showAddressForm[-1] && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-700">
                          {(() => {
                            const selectedAddr = predefinedAddresses.find(addr => addr.id === selectedAddresses[-1]);
                            if (selectedAddr) {
                              return `${selectedAddr.street}, ${selectedAddr.city}, ${selectedAddr.state} ${selectedAddr.zipCode}`;
                            }
                            return '';
                          })()}
                        </div>
                      </div>
                    )}

                    {/* New Address Form */}
                    {showAddressForm[-1] && (
                      <div className="mt-4">
                        <AddressForm
                          address={{
                            name: newAddresses[-1]?.label || '',
                            street: newAddresses[-1]?.street || '',
                            city: newAddresses[-1]?.city || '',
                            state: newAddresses[-1]?.state || '',
                            zipCode: newAddresses[-1]?.zipCode || '',
                          }}
                          onSave={(addressData) => {
                            // Create address object for validation and saving
                            const addressToSave = {
                              id: 'new',
                              label: addressData.name || '',
                              street: addressData.street || '',
                              city: addressData.city || '',
                              state: addressData.state || '',
                              zipCode: addressData.zipCode || ''
                            };
                            
                            // Update the newAddresses state with form data
                            setNewAddresses(prev => ({
                              ...prev,
                              [-1]: addressToSave
                            }));
                            
                            // Pass the address data directly to avoid race condition
                            handleSaveNewAddress(-1, addressToSave);
                          }}
                          onCancel={() => {
                            setShowAddressForm(prev => ({ ...prev, [-1]: false }));
                            setSelectedAddresses(prev => ({ ...prev, [-1]: '' }));
                          }}
                          showDefaultOption={false}
                          submitButtonText="Save Address"
                          cancelButtonText="Cancel"
                          isLoading={savingAddressIndex === -1}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full py-4 rounded-full font-bold text-lg transition-colors mb-6 cursor-pointer ${
                  isSubmitting 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-gray-800 text-white hover:bg-gray-900'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    PROCESSING...
                  </span>
                ) : (
                  'LET BIRDIE GIFTS HANDLE IT'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}